import { useState } from "react";
import { _SecopInformation } from "../../../../core/services/dataBean/EntityCatalog";
import useDataBean from "../../../../core/services/dataBean/useDataBean";
import { BasicButton } from "../../../theme/Component/BasicButton";
import { BasicPage } from "../../../theme/Component/BasicPage";
import { toast } from "react-toastify";
import useModal, { MODAL_LG } from "../../../theme/Component/hooks/useModal";
import { Modal } from "../../../theme/Component/Modal";
import { BasicTable, MASK_MONEY } from "../../../theme/Component/BasicTable";

const EMPTY_VALUES = ["", null, undefined, "Sin Descripcion", "No definido", "No Definido"];

const hasValue = (value: any) => !EMPTY_VALUES.includes(value);

const getValue = (data: Record<string, any>, ...keys: string[]) => {
    for (const key of keys) {
        if (hasValue(data?.[key])) {
            return data[key];
        }
    }

    return "";
};

const formatText = (value: any, fallback = "No definido") => (
    hasValue(value) ? value : fallback
);

const formatCurrency = (value: any) => {
    const num = parseFloat(value);

    if (Number.isNaN(num)) {
        return formatText(value);
    }

    return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
    }).format(num);
};

const formatDate = (dateStr: string) => {
    if (!hasValue(dateStr)) {
        return "No definida";
    }

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
        return dateStr;
    }

    return date.toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const formatDocument = (documentType: any, documentNumber: any) => {
    const safeType = hasValue(documentType) ? documentType : "";
    const safeNumber = formatText(documentNumber);

    return safeType ? `${safeType}: ${safeNumber}` : safeNumber;
};

const formatLocation = (data: Record<string, any>) => {
    const city = getValue(data, "ciudad", "ciudad_entidad");
    const department = getValue(data, "departamento", "departamento_entidad");

    if (city && department) {
        return `${city}, ${department}`;
    }

    if (city || department) {
        return city || department;
    }

    return formatText(getValue(data, "localizacion"));
};

export const SecopContractInfo = () => {
    const { instance, bean, setBean, lista, setLista } = useDataBean(_SecopInformation);

    const [porta, setPorta] = useState<any>({});
    const [filter, setFilter] = useState<any>({});
    const modal = useModal("Contratos Encontrados", MODAL_LG);
    const [page, setPage] = useState(1);
    const pageSize = 20;

    const getLista = (currentPage = page) => {
        instance.getSecopContratosAvanzado(
            filter.Nit,
            filter.supplier,
            filter.contractStatus,
            filter.city,
            filter.contractReference,
            currentPage,
            pageSize
        ).then((resp: any) => {
            if (resp.length === 1) {
                setBean(resp[0].DataBeanProperties);
            } else {
                setBean({});
            }

            setLista(resp);
            setPage(currentPage);
        }).catch((err: any) => toast.error(err));
    };

    const head = [
        { title: "Referencia", property: "referencia_del_contrato", visible: true },
        { title: "Proveedor", property: "proveedor_adjudicado", visible: true },
        { title: "Entidad", property: "nombre_entidad", visible: true },
        { title: "Estado", property: "estado_contrato", visible: true },
        { title: "Ciudad", property: "ciudad", visible: true },
        { title: "Valor", property: "valor_contrato", visible: true, mask: MASK_MONEY },
    ];

    const selectItem = () => {
        setBean({ ...porta });
        modal.close();
    };

    const contractReference = getValue(bean, "referencia_del_contrato");
    const contractObject = getValue(bean, "objeto_contrato", "objeto_del_contrato");
    const contractType = getValue(bean, "tipo_contrato", "tipo_de_contrato");
    const contractModality = getValue(bean, "modalidad_contratacion", "modalidad_de_contratacion");
    const contractState = getValue(bean, "estado_contrato");
    const entityName = getValue(bean, "nombre_entidad", "entidad");
    const supplierName = getValue(bean, "proveedor_adjudicado", "nombre_del_proveedor", "proveedor");
    const supplierDocumentType = getValue(bean, "tipo_doc_proveedor", "tipodocproveedor");
    const supplierDocument = getValue(bean, "documento_proveedor", "doc_proveedor");
    const representativeName = getValue(bean, "nombre_representante", "nombre_representante_legal");
    const representativeDocumentType = getValue(bean, "tipo_id_representante");
    const representativeDocument = getValue(bean, "id_representante", "identificaci_n_representante_legal");

    return (
        <BasicPage titulo="Informacion de Contratos (SECOP)">
            <div className="row">
                <div className="col-md-4">
                    <label>Entidad (NIT)</label>
                    <input
                        className="form-control"
                        type="number"
                        value={filter.Nit}
                        onChange={(e) => {setFilter({ ...filter, Nit: e.target.value })}}
                    />
                </div>
                <div className="col-md-4">
                    <label>Codigo del contrato</label>
                    <input
                        className="form-control"
                        type="text"
                        value={filter.contractReference || ""}
                        onChange={(e) => setFilter({ ...filter, contractReference: e.target.value })}
                    />
                </div>

                <div className="col-md-4">
                    <label>Proveedor (nombre)</label>
                    <input
                        className="form-control"
                        type="text"
                        value={filter.supplier || ""}
                        onChange={(e) => setFilter({ ...filter, supplier: e.target.value })}
                    />
                </div>

                <div className="col-md-4">
                    <label>Estado del contrato</label>
                    <select
                        className="form-select"
                        value={filter.contractStatus || ""}
                        onChange={(e) => setFilter({ ...filter, contractStatus: e.target.value })}
                    >
                        <option value="">Todos</option>
                        <option value="En aprobacion">En aprobacion</option>
                        <option value="Activo">Activo</option>
                        <option value="Liquidado">Liquidado</option>
                        <option value="Modificado">Modificado</option>
                    </select>
                </div>

                <div className="col-md-4">
                    <label>Ciudad</label>
                    <input
                        className="form-control"
                        type="text"
                        value={filter.city || ""}
                        onChange={(e) => setFilter({ ...filter, city: e.target.value })}
                    />
                </div>

                <div className="col-md-4">
                    <BasicButton
                        eventClick={() => getLista(1)}
                        icon="ri-search-line"
                        useMargin
                    >
                        Buscar
                    </BasicButton>
                </div>

                {lista.length > 1 && (
                    <div className="col-md-12">
                        <label>Contratos disponibles</label>
                        <input
                            type="text"
                            className="form-control"
                            aria-label="Contrato seleccionado"
                            placeholder="Seleccionar contrato..."
                            value={contractReference || ""}
                            onClick={() => { modal.open(); }}
                            readOnly
                        />

                        <Modal modal={modal.modal} updateModal={modal.setModal} eventModal={selectItem}>
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <button
                                            className="btn btn-outline-secondary"
                                            disabled={page === 1}
                                            onClick={() => getLista(page - 1)}
                                        >
                                            Anterior
                                        </button>

                                        <span className="fw-bold">
                                            Pagina {page}
                                        </span>

                                        <button
                                            className="btn btn-outline-primary"
                                            disabled={lista.length < pageSize}
                                            onClick={() => getLista(page + 1)}
                                        >
                                            Siguiente
                                        </button>
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <BasicTable body={lista} head={head} rowSet={setPorta} customRows={pageSize} />
                                </div>
                            </div>
                        </Modal>
                    </div>
                )}

                {contractReference ? (
                    <>
                        <div className="card-header py-3">
                            <h5 className="mb-0">
                                <i className="ri-information-line me-2"></i>
                                Detalles SECOP - {contractReference}
                            </h5>
                        </div>

                        <div className="card-body p-4">
                            <div className="mb-4">
                                <h6 className="text-primary border-bottom pb-2 mb-3 fw-bold">Informacion general</h6>

                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="text-muted small mb-1">Objeto del contrato</label>
                                        <p className="fw-medium mb-0">{formatText(contractObject)}</p>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Tipo de contrato</label><br/>
                                        <span className="badge bg-soft-info text-info p-2">
                                            {formatText(contractType)}
                                        </span>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Modalidad</label>
                                        <p className="mb-0">{formatText(contractModality)}</p>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Estado</label>
                                        <p className="mb-0">{formatText(contractState)}</p>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="text-muted small mb-1">Ubicacion</label>
                                        <p className="mb-0">
                                            <i className="ri-map-pin-line me-1"></i>
                                            {formatLocation(bean)}
                                        </p>
                                    </div>

                                    <div className="col-md-12">
                                        <label className="text-muted small mb-1">Entidad</label>
                                        <p className="mb-0">{formatText(entityName)}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h6 className="text-primary border-bottom pb-2 mb-3 fw-bold">Valores y ejecucion</h6>

                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <div className="p-3 bg-light rounded-3 text-center">
                                            <label className="text-muted small mb-1">Valor total</label>
                                            <h5 className="text-success fw-bold mb-0">
                                                {formatCurrency(getValue(bean, "valor_contrato", "valor_del_contrato"))}
                                            </h5>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div className="p-3 bg-light rounded-3 text-center">
                                            <label className="text-muted small mb-1">Pendiente ejecucion</label>
                                            <h5 className="text-warning fw-bold mb-0">
                                                {formatCurrency(getValue(bean, "valor_pendiente_ejecucion", "valor_pendiente_de_ejecucion"))}
                                            </h5>
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div className="p-3 bg-light rounded-3 text-center">
                                            <label className="text-muted small mb-1">Pendiente pago</label>
                                            <h5 className="text-danger fw-bold mb-0">
                                                {formatCurrency(getValue(bean, "valor_pendiente_pago", "valor_pendiente_de_pago"))}
                                            </h5>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h6 className="text-primary border-bottom pb-2 mb-3 fw-bold">Cronograma</h6>

                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="text-muted small mb-1">Fin contrato</label>
                                        <p className="mb-0">{formatDate(getValue(bean, "fecha_fin_contrato", "fecha_de_fin_del_contrato"))}</p>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="text-muted small mb-1">Inicio liquidacion</label>
                                        <p className="mb-0">{formatDate(getValue(bean, "fecha_inicio_liquidacion"))}</p>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="text-muted small mb-1">Fin liquidacion</label>
                                        <p className="mb-0">{formatDate(getValue(bean, "fecha_fin_liquidacion"))}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="row g-3">
                                <div className="col-md-6">
                                    <h6 className="text-primary border-bottom pb-2 mb-3 fw-bold">Contratista</h6>

                                    <div className="p-3 border rounded-3 h-100">
                                        <p className="fw-bold mb-1">{formatText(supplierName)}</p>
                                        <p className="small text-muted mb-0">
                                            {formatDocument(supplierDocumentType, supplierDocument)}
                                        </p>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <h6 className="text-primary border-bottom pb-2 mb-3 fw-bold">Representante legal</h6>

                                    <div className="p-3 border rounded-3 h-100">
                                        <p className="fw-bold mb-1">{formatText(representativeName)}</p>
                                        <p className="small text-muted mb-0">
                                            {formatDocument(representativeDocumentType, representativeDocument)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-5 text-center">
                                <a
                                    href={bean.urlproceso}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-primary rounded-pill"
                                >
                                    Ver proceso en SECOP
                                </a>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center p-5 bg-light rounded-3 mt-2">
                        <i className="ri-search-eye-line display-4 text-muted"></i>
                        <p className="mt-2 mb-0">Sin resultados...</p>
                    </div>
                )}
            </div>
        </BasicPage>
    );
};
